import React from 'react';
import { FaBan } from 'react-icons/fa';
import { Button, PowerIcon } from './Primitives';
import OperationStatus from './OperationStatus';
import {
  ACTION,
  OPERATION_STATUS,
  POWER_STATE,
  actionMeta,
  normalizePowerState,
} from '../utils/constants';
import { visibleActionsForVm, isVmIdentityAmbiguous } from '../utils/format';

/**
 * Pulsanti operativi di una VM. Mostra ESCLUSIVAMENTE le azioni presenti in
 * `availableActions` e supportate dal contratto; nessun pulsante per VM con
 * identità ambigua. Durante un'operazione i pulsanti sono disabilitati (lock,
 * anti doppio-click) e viene mostrato il feedback dell'operazione.
 */
export default function VmActions({
  vm,
  catalogWarnings,
  op,
  busy,
  onRequestAction,
  onDismissOp,
  size = 'normal',
}) {
  const ambiguous = isVmIdentityAmbiguous(vm, catalogWarnings);
  const actions = visibleActionsForVm(vm, catalogWarnings);
  const awaitingTools =
    !busy &&
    op?.status === OPERATION_STATUS.SUCCEEDED &&
    op?.action === ACTION.POWER_ON &&
    normalizePowerState(vm.powerState) === POWER_STATE.ON &&
    actions.length === 0;

  if (ambiguous) {
    return (
      <div
        className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-2.5 py-1.5 text-xs text-gray-500 dark:bg-gray-700/50 dark:text-gray-400"
        title="Azioni non disponibili: identità della VM non verificata"
      >
        <FaBan size={11} aria-hidden="true" />
        Azioni bloccate (identità ambigua)
      </div>
    );
  }

  if (actions.length === 0 && !op) {
    return (
      <span className="text-xs text-gray-400 dark:text-gray-500">
        Nessuna azione disponibile
      </span>
    );
  }

  const btnVariant = { powerOn: 'info', shutdown: 'warning' };
  const btnClass = size === 'small' ? 'px-2.5 py-1 text-xs' : '';

  return (
    <div className="flex flex-col gap-2">
      {actions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {actions.map((action) => {
            const meta = actionMeta(action);
            return (
              <Button
                key={action}
                variant={btnVariant[action] || 'primary'}
                className={btnClass}
                disabled={busy}
                onClick={() => onRequestAction(vm, action)}
                title={meta?.label}
                aria-label={`${meta?.label} — ${vm.name}`}
              >
                <PowerIcon name={meta?.icon} size={14} aria-hidden="true" />
                {meta?.label}
              </Button>
            );
          })}
        </div>
      )}
      {awaitingTools && (
        <Button
          variant="warning"
          className={btnClass}
          disabled
          title="Lo spegnimento sarà disponibile quando VMware Tools risulterà attivo"
        >
          <PowerIcon name="power" size={14} aria-hidden="true" />
          Spegnimento in preparazione
        </Button>
      )}
      {op && (
        <OperationStatus
          op={op}
          onDismiss={onDismissOp ? () => onDismissOp(vm.catalogId) : undefined}
          compact={size === 'small'}
        />
      )}
    </div>
  );
}
